import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { JobSearchCriteria, JobSearchResult, ProcessingDetails } from '@/types/jobSearch';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useSavedJobs } from './useSavedJobs';
import { useJobProcessing } from './useJobProcessing';
import { useLutraPolling } from './useLutraPolling'; // Import the new hook
import { invokeSearchAgent } from '@/utils/jobSearchUtils';

// Constants FRONTEND_POLLING_INTERVAL and MAX_FRONTEND_POLLING_DURATION are moved to useLutraPolling.ts

export const useJobSearch = () => {
  const { user } = useAuth();
  const [searchResults, setSearchResults] = useState<JobSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [processingDetails, setProcessingDetails] = useState<ProcessingDetails | null>(null);
  
  // Polling refs (pollingTimeoutRef, frontendPollingStartTimeRef, currentProviderIdRef) are moved to useLutraPolling.ts

  const { 
    savedJobs, 
    isFetchingSavedJobs, 
    saveJob: saveJobToSavedJobs, 
    unsaveJob, 
    isSavingJobId 
  } = useSavedJobs();

  const {
    crawlJobDetails,
    validateJobData,
    crawlingJobId,
    validatingJobId,
  } = useJobProcessing({ searchResults, setSearchResults, setError });

  // Instantiate the new polling hook
  const { startPolling, clearPolling: clearLutraPolling } = useLutraPolling({
    setSearchResults,
    setError,
    setIsLoading,
    setProcessingDetails,
  });

  const saveJob = async (job: JobSearchResult) => {
    if (!user) {
      toast.error("You must be logged in to save jobs.");
      return;
    }
    return saveJobToSavedJobs(job);
  };

  const searchJobs = async (criteria: JobSearchCriteria, providerId: string) => {
    setIsLoading(true);
    setError(null);
    setSearchResults([]);
    setProcessingDetails(null);
    clearLutraPolling();

    try {
      console.log('[useJobSearch] Initial search with criteria:', criteria, 'Provider:', providerId);
      const response = await invokeSearchAgent({ criteria, providerId });
      console.log('[useJobSearch] Raw response from initial invokeSearchAgent:', JSON.stringify(response, null, 2));

      if (response.error) {
        // Handle different types of errors with appropriate user messaging
        if (response.errorType === 'MISSING_CREDENTIALS') {
          setError(response.error);
          toast.error("Lutra credentials not configured", {
            description: response.fallbackSuggestion || "Please contact your administrator to configure Lutra credentials."
          });
        } else if (response.errorType === 'SEARCH_REQUEST_FAILED') {
          setError(response.error);
          toast.error("Search service temporarily unavailable", {
            description: response.fallbackSuggestion || "Please try again later or use a different search provider."
          });
        } else {
          setError(response.error);
          toast.error(`Search error: ${response.error}`);
        }

        if (response.processingDetails) {
          setProcessingDetails(response.processingDetails);
        }
        
        setIsLoading(false);
        return;
      }

      if (response.processingDetails?.runDetails?.poll_results_url) {
        setProcessingDetails(response.processingDetails);
        startPolling(response.processingDetails.runDetails.poll_results_url, providerId);
      } else if (response.results) {
        setSearchResults(response.results);
        if (response.results.length === 0) {
          toast.info("No jobs found for your criteria.");
        } else {
          toast.success(`Found ${response.results.length} jobs!`);
        }
        setProcessingDetails(null);
        setIsLoading(false);
      } else {
        throw new Error('Unexpected response structure from search agent.');
      }

    } catch (e: any) {
      console.error(`[useJobSearch] Error in searchJobs:`, e);
      const errorMessage = e.message || `Failed to search jobs using ${providerId}.`;
      setError(errorMessage);
      toast.error(errorMessage);
      setProcessingDetails(null);
      setIsLoading(false);
      clearLutraPolling();
    }
  };

  useEffect(() => {
    return () => {
      clearLutraPolling(); // Use the new hook's clear method
    };
  }, [clearLutraPolling]);

  return { 
    searchResults, 
    isLoading, 
    error,
    processingDetails, 
    searchJobs, 
    crawlJobDetails, 
    crawlingJobId,
    validateJobData,
    validatingJobId,
    savedJobs,
    isFetchingSavedJobs,
    saveJob,
    unsaveJob,
    isSavingJobId
  };
};
